// src/pages/DossierPage.jsx
import React, { useMemo, useState } from "react";
import { Routes, Route, useParams, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Separator from "@radix-ui/react-separator";

import DossierSidebar from "../components/dossier/DossierSidebar";
import BlockCard from "../components/dossier/BlockCard";
import PDFPreview, { assetURL } from "../components/dossier/PDFPreview";
import SessionsIndexPage from "./SessionsIndexPage.jsx";
import SessionDetailPage from "./SessionDetailPage.jsx";
import DossierProjectPage from "./DossierProjectPage.jsx";
import ProjectDetailPage from "./ProjectDetailPage.jsx";

import { styles } from "../styles";
import { getDossierCatalog } from "../data/dossierCatalog";
import { buildDossierPath, getDossierModeFromSearch, isExpressDossierMode } from "../data/dossierModes";
import { parseSessionNumber } from "../lib/dossierService";
import DossierEmptyState from "../components/dossier/DossierEmptyState";

/* ------------------------ Portadas ------------------------ */
const PROJECT_COVER_URL = "/images/dossier/projecte-cover.png";
const PROJECT_COVER_FALLBACK = "/images/dossier/cover-sessions.png";
const COVER_SESSIONS = "/images/dossier/cover-sessions.png";
const PDF_GUIA_PROF = "/docs/dossier-guia-professorat.pdf";
const PDF_ALUMNAT = "/docs/dossier-alumnat-livingsoils.pdf";

/* --------------------------------- Utils -------------------------------- */
const isPdf = (p = "") => /\.pdf$/i.test(p);
const isImg = (p = "") => /\.(png|jpe?g|webp|gif)$/i.test(p);
const isVideo = (p = "") => /\.(mp4|webm)$/i.test(p);
const extToType = (path = "") => {
  const p = path.toLowerCase();
  if (isPdf(p)) return "pdf";
  if (isVideo(p)) return "video";
  if (isImg(p)) return "image";
  if (p.endsWith(".docx")) return "docx";
  return "file";
};

/** Quita los prefijos "projecte_" o "projecte-" al inicio del slug */
const stripProjectePrefix = (k = "") => (k || "").replace(/^projecte[_-]*/i, "");

const stripNumber = (s = "") => s.replace(/^\s*\d+\s*[-._)]*\s*/u, "").trim();
const removeDiacritics = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const norm = (s = "") => removeDiacritics(stripNumber(s)).toLowerCase();
const detectCategoryKey = (rawTitle = "") => {
  const n = norm(rawTitle);
  if (
    n.includes("continguts previs") ||
    n.includes("contenidos previos") ||
    n.includes("previous content")
  )
    return "previs";
  if (
    n.includes("material alumnat") ||
    n.includes("material alumnado") ||
    n.includes("student materials")
  )
    return "alumnat";
  if (
    n.includes("guia didactica professorat") ||
    n.includes("guia didactica profesorado") ||
    n.includes("teacher")
  )
    return "guia";
  return "other";
};
const ORDER_KEYS = ["previs", "alumnat", "guia"];

/* ---------- Miniatura de sesión (igual que en SessionsIndexPage) ---------- */
function SessionThumb({ id, title }) {
  const [src, setSrc] = useState(`/images/dossier/sessions/${id}.png`);
  const [tried, setTried] = useState({ png: true, jpg: false, cover: false });

  const onError = () => {
    if (tried.png && !tried.jpg) {
      setSrc(`/images/dossier/sessions/${id}.jpg`);
      setTried((s) => ({ ...s, jpg: true }));
    } else if (!tried.cover) {
      setSrc(COVER_SESSIONS);
      setTried((s) => ({ ...s, cover: true }));
    }
  };

  return (
    <img
      src={src}
      alt={title ? `Sessió ${id}: ${title}` : `Sessió ${id}`}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
      onError={onError}
    />
  );
}

/* ---------- Inferidor de fase de projecte → slug exacto con _ y sin "projecte_" ---------- */
const deaccent = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Devuelve una clave tipo "idees_genials_fase_2" (sin "projecte_") a partir de un texto en snake_case */
function pickPhaseFromSnake(snake = "") {
  const clean = stripProjectePrefix(snake);
  const m = clean.match(/^([a-z0-9_]*?fase_[0-9]+)/i);
  if (m && m[1]) return m[1];
  return clean || null;
}

function inferProjectPhaseKey(path = "", label = "") {
  const after = path.split("/projecte/")[1] || "";
  const baseSeg = (after.split(/[?#]/)[0] || "").replace(/\.[a-z0-9]+$/i, "");
  if (baseSeg) {
    const segSnake = deaccent(baseSeg).replace(/[^a-z0-9]+/g, "_");
    const picked = pickPhaseFromSnake(segSnake);
    if (picked) return picked;
  }
  const file = (path.split("/").pop() || "").replace(/\.[a-z0-9]+$/i, "");
  if (file) {
    const snake = deaccent(file).replace(/[^a-z0-9]+/g, "_");
    const picked = pickPhaseFromSnake(snake);
    if (picked) return picked;
  }
  const snakeLabel = deaccent(label).replace(/[^a-z0-9]+/g, "_");
  const picked = pickPhaseFromSnake(snakeLabel);
  if (picked) return picked;
  const fallback =
    snakeLabel.split("_").filter(Boolean).slice(0, 2).join("_") || null;
  return fallback;
}

/* ------------------------------ Portada root ----------------------------- */
function Overview({ mode, catalog }) {
  const { t } = useTranslation();
  const blocks = catalog?.blocks || [];

  if (isExpressDossierMode(mode)) {
    const expressStudentDossier = catalog?.standalone?.[0];
    return (
      <>
        <style>{`
          .ls-animated-gradient {
            background: linear-gradient(90deg,#fde68a,#bbf7d0,#bfdbfe,#fde68a);
            background-size: 300% 100%;
            animation: lsGradientShift 6s linear infinite;
            box-shadow: 0 12px 30px rgba(0,0,0,.06);
          }
          @keyframes lsGradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div className="w-full space-y-3">
          <a
            href={assetURL(expressStudentDossier?.path || "")}
            download
            className="ls-animated-gradient relative block w-full rounded-2xl border px-6 py-6 sm:py-8 text-center font-extrabold tracking-wide"
          >
            {expressStudentDossier?.label || t("ui.dossier.expressStudentDossier", "Dossier de l’alumnat de LivingSoils Express")}
          </a>
        </div>

        <section className="space-y-4 pt-8">
          <div className="grid grid-cols-[auto,1fr] items-center gap-3">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-gray-800">
              {t("ui.dossier.expressTeacherMaterials", "Material professorat")}
            </h2>
            <Separator.Root className="bg-gray-200 h-px w-full" />
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {(catalog?.sessions || []).map((session) => (
              <article
                key={session.id}
                className="rounded-2xl border bg-white overflow-hidden hover:shadow-md transition flex flex-col"
              >
                <div className="aspect-[3/2] bg-gray-100 overflow-hidden">
                  <SessionThumb id={session.id} title={session.title} />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-semibold text-[15px]">{session.title}</h3>
                  <p className="text-sm mt-2 text-gray-600">
                    {t("ui.dossier.expressSessionResourceCount", {
                      count: session.resources?.length || 0,
                      defaultValue: "{{count}} recursos",
                    })}
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to={buildDossierPath(`/dossier/sessions/${session.id}`, mode)}
                      className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-white hover:bg-gray-50 transition"
                    >
                      {t("ui.common.viewDetails", "Veure detall")}
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <style>{`
        .ls-animated-gradient {
          background: linear-gradient(90deg,#fde68a,#bbf7d0,#bfdbfe,#fde68a);
          background-size: 300% 100%;
          animation: lsGradientShift 6s linear infinite;
          box-shadow: 0 12px 30px rgba(0,0,0,.06);
        }
        @keyframes lsGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="space-y-10">
        {/* ❌ Carrusel eliminado */}
        {/* Grid de blocs */}
        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {blocks.map((b) => {
            const title = t(`dossier_didactic.blocks.${b.slug}.title`, {
              defaultValue: b.title,
            });
            return (
              <BlockCard key={b.slug} to={buildDossierPath(`/dossier/bloc/${b.slug}`, mode)} title={title} />
            );
          })}
        </section>

        {/* Botones anchos: Guia professorat + Dossier alumnat */}
        <div className="w-full space-y-3">
          <a
            href={PDF_GUIA_PROF}
            download
            className="ls-animated-gradient relative block w-full rounded-2xl border px-6 py-6 sm:py-8 text-center font-extrabold tracking-wide"
          >
            {t("ui.dossier.teacherGuideShort", "Guia professorat (PDF)")}
          </a>

          <a
            href={PDF_ALUMNAT}
            download
            className="ls-animated-gradient relative block w-full rounded-2xl border px-6 py-6 sm:py-8 text-center font-extrabold tracking-wide"
          >
            {t("materials.studentDossier", "Dossier alumnat LivingSoils (PDF)")}
          </a>
        </div>
      </div>
    </>
  );
}

/* ------------------------------ Card preview ----------------------------- */
function CardPreview({ item }) {
  const type = item.type || extToType(item.path);

  const looksLikeSession =
    /(sessió|sesion|sesión|session)\s*\d+/i.test(item.label || "") ||
    /(sessió|sesion|sesión|session)[-_\s]?\d+/i.test(item.path || "") ||
    /\bsession[-_\s]?\d+\b/i.test(item.path || "") ||
    /\bsessio[-_\s]?\d+\b/i.test(item.path || "");
  const sessionId =
    parseSessionNumber(item.label || "") || parseSessionNumber(item.path || "");

  if (type === "docx" && looksLikeSession && sessionId) {
    return (
      <div className="relative w-full aspect-[3/2] bg-gray-100 overflow-hidden">
        <SessionThumb id={sessionId} title={item.label} />
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-black/70 text-white">
          DOCX
        </span>
      </div>
    );
  }

  if (type === "docx" && item.cover) {
    return (
      <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        <img
          src={item.cover}
          alt={item.label || "cover"}
          className="w-full h-full object-cover scale-[0.91] origin-center"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = PROJECT_COVER_FALLBACK;
          }}
        />
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-black/70 text-white">
          DOCX
        </span>
      </div>
    );
  }

  if (type === "pdf") {
    // 🔍 Detectar si es una rúbrica
    const looksLikeRubriques =
      /rúbriques/i.test(item.label || "") ||
      /rubriques/i.test(item.label || "") ||
      /rúbriques/i.test(item.path || "") ||
      /rubriques/i.test(item.path || "");

    // Si lo es, usamos portada estática
    const coverSrc = looksLikeRubriques
      ? "/images/dossier/rubriques-cover.png"
      : null;

    return (
      <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        {coverSrc ? (
          // 🔗 Envolvemos la portada en un enlace directo al PDF
          <a
            href={assetURL(item.path)}
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={coverSrc}
              alt={item.label || "Rúbriques d’avaluació"}
              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition"
            />
          </a>
        ) : (
          // 🧩 Para los PDFs normales, preview directo
          <PDFPreview src={assetURL(item.path)} title={item.label} />
        )}

        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-emerald-600 text-white">
          PDF
        </span>
      </div>
    );
  }

  if (type === "image") {
    return (
      <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        <img
          src={assetURL(item.path)}
          alt={item.label || "image"}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-black/70 text-white">
          IMG
        </span>
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[3/4] bg-gray-100 flex items-center justify-center overflow-hidden">
      <div className="text-xs sm:text-sm text-gray-500 px-3 text-center select-none">
        {item.label}
      </div>
      <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-black/70 text-white">
        {(type || "file").toUpperCase()}
      </span>
    </div>
  );
}

/* -------------------------------- Resource -------------------------------- */
function ResourceCard({ item, limitWidth = false, mode }) {
  const { t } = useTranslation();
  const type = item.type || extToType(item.path);

  const label = item.label || "";
  const path = item.path || "";
  const categoryTitle = item._categoryTitle || "";

  const n = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const labelN = n(label);
  const pathN = n(path);
  const catN = n(categoryTitle);

  const isProjectContext =
    catN.includes("projecte") ||
    catN.includes("proyecto") ||
    catN.includes("project") ||
    labelN.includes("projecte") ||
    labelN.includes("proyecto") ||
    labelN.includes("project") ||
    pathN.includes("/projecte/");

  const looksLikeSession =
    /(sessió|sesion|sesión|session)\s*\d+/i.test(label) ||
    /(sessió|sesion|sesión|session)[-_\s]?\d+/i.test(path) ||
    /\bsession[-_\s]?\d+\b/i.test(path) ||
    /\bsessio[-_\s]?\d+\b/i.test(path);

  let detailTo = null;
  if (type === "docx") {
    if (looksLikeSession) {
      const id = parseSessionNumber(label) || parseSessionNumber(path);
      detailTo = id
        ? buildDossierPath(`/dossier/sessions/${id}`, mode)
        : buildDossierPath(`/dossier/sessions`, mode);
    } else if (isProjectContext) {
      const phaseKey = item.phaseKey || inferProjectPhaseKey(path, label) || null;
      detailTo = phaseKey
        ? buildDossierPath(`/dossier/projecte/${phaseKey}`, mode)
        : buildDossierPath(`/dossier/projecte`, mode);
    }
  }

  const docxCover = type === "docx" && isProjectContext ? PROJECT_COVER_URL : null;

  return (
    <div className={limitWidth ? "w-full max-w-[520px]" : "w-full"}>
      <article
        className="rounded-2xl border bg-white overflow-hidden hover:shadow-lg transition"
        title={item.label}
      >
        <CardPreview item={{ ...item, cover: docxCover }} />
        <div className="p-4">
          <div className="text-sm font-medium leading-snug">{item.label}</div>
          {item._block && (
            <div className="text-xs text-gray-500 mt-1">{item._block}</div>
          )}
          <div className="mt-2 flex gap-2 flex-wrap">
            {type === "docx" && detailTo && (
              <Link
                to={detailTo}
                className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-white hover:bg-gray-50 transition"
              >
                {t("ui.common.viewDetails", "Veure detall")}
              </Link>
            )}
            <a
              className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl ${
                type === "docx"
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-white hover:bg-gray-50"
              } transition`}
              href={assetURL(item.path)}
              download
            >
              {t("dossier_didactic.download", "Descarregar")}
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}

/* ------------------------------ Category Shelf --------------------------- */
function CategoryShelf({ title, items, nowrap = false, mode }) {
  if (!items?.length) return null;
  const key = detectCategoryKey(title); // previs | alumnat | guia | other
  const single = items.length === 1;

  return (
    <section id={key !== "other" ? key : undefined} className="space-y-4">
      <div className="grid grid-cols-[auto,1fr] items-center gap-3">
        <h3
          className={[
            "text-lg sm:text-xl md:text-2xl font-extrabold tracking-tight text-gray-800 uppercase",
            nowrap ? "whitespace-nowrap" : "",
          ].join(" ")}
        >
          {title}
        </h3>
        <Separator.Root className="bg-gray-200 h-px w-full" />
      </div>

      {single ? (
        <div className="flex justify-center">
          <ResourceCard
            item={{ ...items[0], _categoryTitle: title }}
            limitWidth
            mode={mode}
          />
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6">
          {items.map((it, idx) => (
            <ResourceCard
              key={`${title}-${idx}-${it.label}`}
              item={{ ...it, _categoryTitle: title }}
              mode={mode}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* ------------------------- Detalle de un Bloc ------------------------ */
function BlocDetail({ mode, catalog }) {
  const { t } = useTranslation();
  const { blocSlug } = useParams();
  const blocks = catalog?.blocks || [];

  if (isExpressDossierMode(mode)) {
    return <DossierEmptyState />;
  }

  const bloc = blocks.find((b) => b.slug === blocSlug);

  if (!bloc) {
    return (
      <div className="rounded-2xl border bg-white p-6">
        <p className="text-red-600">
          {t("ui.dossier.errors.blockNotFound", "Bloc no trobat.")}
        </p>
        <Link className="underline mt-2 inline-block" to={buildDossierPath("/dossier", mode)}>
          {t("ui.dossier.links.backOverview", "← Tornar a la vista general")}
        </Link>
      </div>
    );
  }

  const groups = { previs: null, alumnat: null, guia: null };
  (bloc.categories || []).forEach((cat) => {
    const key = detectCategoryKey(cat.title);
    if (key in groups) groups[key] = cat;
  });

  const ordered = ORDER_KEYS
    .map((k) => groups[k])
    .filter(Boolean)
    .map((cat, idx) => {
      const k = detectCategoryKey(cat.title);
      const baseName = t(`dossier_didactic.categories.${k}`);
      const hasProjectTag =
        norm(cat.title).includes("project") ||
        norm(cat.title).includes("projecte") ||
        norm(cat.title).includes("proyecto");
      const tag =
        k === "guia" && hasProjectTag
          ? ` ${t("dossier_didactic.categories.projectTag")}`
          : "";
      const renamed = `${idx + 1} ${baseName}${tag}`;
      return { ...cat, title: renamed };
    });

  const localizedTitle = t(
    `dossier_didactic.blocks.${bloc.slug}.title`,
    { defaultValue: bloc.title }
  );

  return (
    <div className="space-y-10">
      <header className="text-center space-y-4">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900">
          {localizedTitle}
        </h2>
        <Separator.Root className="bg-gray-300 h-px w-full" />
      </header>

     <div className="space-y-12">
  {ordered.map((cat, i) => (
    <CategoryShelf
      key={`${bloc.slug}-cat-${i}`}
      title={cat.title}
      items={cat.items || []}
      nowrap={false}
      mode={mode}
    />
  ))}

  {/* 🔥 Mostrar también las categorías no clasificadas (como rúbriques) */}
  {(bloc.categories || [])
    .filter((cat) => !["previs", "alumnat", "guia"].includes(detectCategoryKey(cat.title)))
    .map((cat, i) => (
      <CategoryShelf
        key={`${bloc.slug}-extra-${i}`}
        title={cat.title}
        items={cat.items || []}
        nowrap={false}
        mode={mode}
      />
    ))}
</div>
    </div>
  );
}


/* -------------------- Muro global de PDFs -------------------- */
function DocumentsWall({ mode, catalog }) {
  const { t } = useTranslation();
  const blocks = catalog?.blocks || [];
  const standalone = catalog?.standalone || [];

  const pdfItems = useMemo(() => {
    const out = [];
    blocks.forEach((b) => {
      const localizedBlock = t(`dossier_didactic.blocks.${b.slug}.title`, {
        defaultValue: b.title,
      });
      b.categories?.forEach((c) =>
        c.items?.forEach((it) => {
          if (isPdf(it.path)) out.push({ ...it, _block: localizedBlock });
        })
      );
    });
    standalone?.forEach((it) => {
      if (isPdf(it.path))
        out.push({
          ...it,
          _block: t("ui.dossier.resources.guides", "Guia i vídeos"),
        });
    });
    return out;
  }, [t, blocks, standalone]);

  if (isExpressDossierMode(mode)) {
    return <DossierEmptyState />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[auto,1fr] items-center gap-3">
        <h2 className="text-2xl font-bold">
          {t("dossier_didactic.categories.documents")}
        </h2>
        <Separator.Root className="bg-gray-200 h-px w-full" />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6">
        {pdfItems.map((it, idx) => (
          <div key={`${idx}-${it.label}`} className="w-full">
            <article className="rounded-2xl border bg-white overflow-hidden hover:shadow-lg transition">
              <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
                <PDFPreview src={assetURL(it.path)} title={it.label} />
                <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-md bg-emerald-600 text-white">
                  PDF
                </span>
              </div>
              <div className="p-4">
                <div className="text-sm font-medium leading-snug">{it.label}</div>
                <div className="text-xs text-gray-500 mt-1">{it._block}</div>
                <a
                  className="mt-2 inline-block text-[12px] underline text-emerald-700"
                  href={assetURL(it.path)}
                  download
                >
                  {t("dossier_didactic.download", "Descarregar")}
                </a>
              </div>
            </article>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- Página raíz --------------------------- */
export default function DossierPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const mode = getDossierModeFromSearch(location.search);
  const materialLocale = i18n.resolvedLanguage || i18n.language;
  const catalog = getDossierCatalog(mode, materialLocale);
  const pageTitle = isExpressDossierMode(mode)
    ? t("ui.dossier.modes.express", "LivingSoils Express")
    : t("ui.dossier.title", "Materials Didàctics");

  return (
    <div className="bg-gradient-to-b from-green-50 to-white opacity-90 min-h-screen relative">
      <div className="blob2" />
      <DossierSidebar mode={mode} />

      {/* Contenido */}
      <div className="mx-auto max-w-[1500px] lg:pl-[18rem] xl:pl-[19rem] px-3 sm:px-4 pr-6 py-28">
        <h1
          className={`
            mb-6 text-center leading-tight break-words
            max-w-[1100px] mx-auto
            ${styles?.heroHeadText || "text-4xl sm:text-5xl lg:text-6xl font-extrabold"}
          `}
          style={{ hyphens: "auto", wordBreak: "break-word", lineHeight: 1.08 }}
        >
          {pageTitle}
        </h1>

        <Separator.Root className="bg-gray-300 h-px w-full mb-10" />

        <Routes>
          <Route index element={<Overview mode={mode} catalog={catalog} />} />
          <Route path="documents" element={<DocumentsWall mode={mode} catalog={catalog} />} />
          <Route path="bloc/:blocSlug" element={<BlocDetail mode={mode} catalog={catalog} />} />
          <Route path="bloc/:blocSlug/sessions" element={<SessionsIndexPage mode={mode} />} />

          {/* Índice y detalle de SESSIONS */}
          <Route path="sessions" element={<SessionsIndexPage mode={mode} />} />
          <Route path="sessions/:sessionId" element={<SessionDetailPage mode={mode} />} />

          {/* Índice y detalle del PROJECTE */}
          <Route path="projecte" element={<DossierProjectPage mode={mode} />} />
          <Route path="projecte/:phaseKey" element={<ProjectDetailPage mode={mode} />} />

          <Route
            path="*"
            element={
              <div className="rounded-2xl border bg-white p-6">
                <p className="text-gray-700">
                  {t("ui.common.routeNotFound", "Ruta no trobada.")}{" "}
                  <Link className="underline" to={buildDossierPath("/dossier", mode)}>
                    {t("ui.dossier.links.back", "Tornar al dossier")}
                  </Link>
                  .
                </p>
              </div>
            }
          />
        </Routes>
      </div>
    </div>
  );
}
